import { Mic, Brain } from "lucide-react";

// Inject keyframes once at module load — never touches DOM again
if (typeof document !== "undefined" && !document.getElementById("ripple-panel-styles")) {
  const style = document.createElement("style");
  style.id = "ripple-panel-styles";
  // Only animate transform + opacity — both are GPU-composited, zero layout/paint cost
  style.textContent = `
    @keyframes ripple-panel {
      0%, 100% { transform: scale(1);   opacity: 1;   }
      50%       { transform: scale(1.2); opacity: 0.8; }
    }
    @keyframes logo-pulse {
      0%, 100% { opacity: 0.7; }
      50%       { opacity: 1;  }
    }
  `;
  document.head.appendChild(style);
}

const RINGS = [
  { size: "25%",  zIndex: 99, delay: "0s"   },
  { size: "40%",  zIndex: 98, delay: "0.2s" },
  { size: "60%",  zIndex: 97, delay: "0.4s" },
  { size: "80%",  zIndex: 96, delay: "0.6s" },
  { size: "100%", zIndex: 95, delay: "0.8s" },
];

const DARK_BG = "linear-gradient(0deg, rgba(50,50,50,0.2) 0%, rgba(100,100,100,0.2) 100%)";
const DARK_BORDERS = [
  "rgb(104,100,100)",
  "rgba(100,100,100,0.8)",
  "rgba(100,100,100,0.6)",
  "rgba(100,100,100,0.4)",
  "rgba(100,100,100,0.2)",
];
const LIGHT_BG = [
  "rgba(243, 241, 248, 0.93)",
  "rgba(106,111,234,0.25)",
  "rgba(179,107,203,0.15)",
  "rgba(138,115,214,0.08)",
  "rgba(90,62,92,0.04)",
];

const StatusLabel = ({ icon: Icon, label, labelColor }) => (
  <div className={`flex items-center gap-2 ${labelColor}`}>
    <Icon className="w-5 h-5 text-red-500 animate-pulse" />
    <span className="text-xl font-light">{label}</span>
    <div className="flex gap-1">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="w-1.5 h-1.5 bg-red-500 rounded-full animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  </div>
);

const RippleAnimationPanel = ({ isPlaying, isRecording, isNextQuestionLoading, theme: themeValue }) => {
  const isLight = themeValue === "light";
  const isActive = isPlaying || isRecording || isNextQuestionLoading;

  const rippleDuration = isPlaying ? "1s" : isRecording ? "4s" : isNextQuestionLoading ? "4s" : "2s";
  const playState = isActive ? "running" : "paused";

  return (
    <div
      className={`w-full h-full flex flex-col items-center justify-center rounded-2xl relative ${
        isLight ? "bg-white shadow-sm border" : "glass-effect backdrop-blur-md shadow-lg"
      }`}
      style={isPlaying ? { boxShadow: "0 0 0 3px rgba(79,102,255,0.7), 0 0 20px rgba(79,102,255,0.35)", outline: "2px solid #6a6fea" } : {}}
    >
      {/* Ripple container */}
      <div
        style={{
          position: "relative",
          width: "min(50%, 50vh)",
          aspectRatio: "1 / 1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          overflow: "hidden",
          borderRadius: "50%",
          // Promote to its own compositor layer — keeps ring animations off the main thread
          willChange: "transform",
        }}
      >
        {RINGS.map((ring, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              width: ring.size,
              aspectRatio: "1 / 1",
              borderRadius: "50%",
              background: isLight ? LIGHT_BG[i] : DARK_BG,
              border: isLight ? "none" : `1px solid ${DARK_BORDERS[i]}`,
              // box-shadow kept static (not in keyframe) — no repaint per frame
              boxShadow: isLight ? "none" : "rgba(0,0,0,0.3) 0 10px 10px 0",
              // backdropFilter removed — was repainting entire stacking context every frame
              zIndex: ring.zIndex,
              animation: `ripple-panel ${rippleDuration} infinite ease-in-out ${ring.delay}`,
              animationPlayState: playState,
            }}
          />
        ))}

        {/* Center logo */}
        <img
          src="/Homepagelogo.png"
          alt="GenieHire"
          style={{
            position: "absolute",
            inset: 0,
            margin: "auto",
            width: 30,
            height: 45,
            objectFit: "contain",
            zIndex: 999,
            animation: "logo-pulse 2s infinite ease-in-out",
            animationPlayState: playState,
          }}
        />
      </div>

      {/* Fixed-height status slot — prevents layout shift */}
      <div style={{ height: 32, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 16 }}>
        {isRecording && !isPlaying && !isNextQuestionLoading && (
          <StatusLabel icon={Mic} label="Listening" labelColor={isLight ? "text-gray-700" : "text-[#D9D9D9]"} />
        )}
        {isNextQuestionLoading && !isPlaying && (
          <StatusLabel icon={Brain} label="Analyzing response" labelColor={isLight ? "text-gray-700" : "text-[#D9D9D9]"} />
        )}
      </div>
    </div>
  );
};

export default RippleAnimationPanel;
